import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';
import { useAuth0 } from '@auth0/auth0-react';
import { SubscriptionTierEnum } from '../common/enums';
import LoginGateway from './LoginGateway';
import ToolsHome, { SlateInfo, LeagueSlateInfo } from './ToolsHome';

interface AuthCheckResponse {
  authTier: string;
  userId: string;
  result: boolean;
}

const LandingPage: React.FC = () => {
  const { isAuthenticated, isLoading: authLoading, user } = useAuth0();
  const history = useHistory();
  const [subscriptionLoading, setSubscriptionLoading] = useState(false);
  const [subscribedLeagues, setSubscribedLeagues] = useState<string[]>([]);
  const [isAllAccess, setIsAllAccess] = useState(false);
  const [leagueSlateInfo, setLeagueSlateInfo] = useState<Record<string, LeagueSlateInfo>>({});

  // Fetch slate info for all leagues
  useEffect(() => {
    const fetchSlateInfo = async () => {
      try {
        const apiUrl = process.env.REACT_APP_API_URL;
        const response = await fetch(
          `${apiUrl}/api/contests/getPreContestSlateInfo?app=DATAHUB`
        );

        if (response.ok) {
          const slates: SlateInfo[] = await response.json();
          const now = new Date();

          // Group slates by sport and find the next upcoming slate for each
          const slatesByLeague: Record<string, LeagueSlateInfo> = {};

          slates.forEach((slate) => {
            const sport = slate.sport.toUpperCase();
            const slateTime = new Date(slate.startTime);
            const isLive = slateTime <= now;
            const gameCount = slate.matchupInfo?.length || 0;

            // Only consider DK slates for consistency, or first available
            if (slate.site !== 'DK' && slatesByLeague[sport]?.nextSlate?.site === 'DK') {
              return;
            }

            if (!slatesByLeague[sport]) {
              slatesByLeague[sport] = {
                sport,
                nextSlate: slate,
                isLive,
                gameCount,
              };
            } else {
              const existingTime = new Date(slatesByLeague[sport].nextSlate!.startTime);
              // Prefer the earliest upcoming slate, or earliest live slate
              if (slateTime < existingTime) {
                slatesByLeague[sport] = {
                  sport,
                  nextSlate: slate,
                  isLive,
                  gameCount,
                };
              }
            }
          });

          setLeagueSlateInfo(slatesByLeague);
        }
      } catch (error) {
        console.error('Failed to fetch slate info:', error);
      }
    };

    fetchSlateInfo();
  }, []);

  // Check subscription status when authenticated
  useEffect(() => {
    const checkSubscription = async () => {
      if (!isAuthenticated || !user?.email) return;

      setSubscriptionLoading(true);
      try {
        const apiUrl = process.env.REACT_APP_API_URL;
        const leagues = ['NFL', 'NBA', 'NHL', 'MMA', 'NASCAR', 'CFB', 'MLB', 'PGA'];
        const accessibleLeagues: string[] = [];

        // Check each league's subscription status
        for (const league of leagues) {
          try {
            const leagueResponse = await fetch(
              `${apiUrl}/api/auth/stripe?sport=${league}&email=${encodeURIComponent(user.email)}&appName=DATAHUB`
            );
            if (leagueResponse.ok) {
              const leagueData: AuthCheckResponse = await leagueResponse.json();
              if (leagueData.authTier && leagueData.authTier !== SubscriptionTierEnum[SubscriptionTierEnum.NONE]) {
                accessibleLeagues.push(league);
              }
            }
          } catch (e) {
            console.error(`Failed to check subscription for ${league}:`, e);
          }
        }

        // If user has no subscriptions at all, redirect to pricing
        if (accessibleLeagues.length === 0) {
          window.location.href = 'https://stokastic.com/pricing';
          return;
        }

        setSubscribedLeagues(accessibleLeagues);

        // Check if user has All Access (subscribed to all leagues)
        setIsAllAccess(accessibleLeagues.length >= 6); // Approximate check
      } catch (error) {
        console.error('Failed to check subscription:', error);
      } finally {
        setSubscriptionLoading(false);
      }
    };

    checkSubscription();
  }, [isAuthenticated, user?.email, history]);

  // Show loading state
  if (authLoading || subscriptionLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100vh"
        bgcolor="#0A0A0A"
      >
        <CircularProgress sx={{ color: 'rgba(255, 255, 255, 0.70)' }} />
      </Box>
    );
  }

  // Show login gateway for unauthenticated users
  if (!isAuthenticated) {
    return <LoginGateway />;
  }

  // Show tools home for authenticated users with subscription
  return (
    <ToolsHome
      subscribedLeagues={subscribedLeagues}
      isAllAccess={isAllAccess}
      leagueSlateInfo={leagueSlateInfo}
    />
  );
};

export default LandingPage;
